import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { euros, fecha } from "../lib/formato";
import { Etiqueta, Modal, Panel, Vacio } from "./piezas";
import { useIdioma, type Variables } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Lo que el club paga por el programa, visto desde el club.
 *
 * Esto no existía: el proveedor sabía cuándo acababa la prueba, cuánto se
 * cobraba y qué pagos había registrados, y el club no sabía nada de nada. Si le
 * cobras cuarenta euros al mes, lo mínimo es que pueda ver por qué y desde
 * cuándo, y sacar un justificante para su carpeta.
 */

interface Pago {
  id: string;
  fecha: string;
  importeCentimos: number;
  periodoHasta: string;
  metodo: string;
}

interface DatosSuscripcion {
  estado: string;
  hasta: string | null;
  diasRestantes: number | null;
  diasDeRetraso: number;
  enGracia: boolean;
  bloqueado: boolean;
  cuotaMensualCentimos: number;
  club: { nombre: string; cif: string | null; direccion: string | null; ciudad: string | null; cp: string | null; provincia: string | null };
  proveedor: { nombre: string; nif: string | null; direccion: string | null; email: string | null } | null;
  pagos: Pago[];
  puedeVerPagos: boolean;
}

const ETIQUETA_ESTADO: Record<string, { texto: Clave; tipo: string }> = {
  PRUEBA: { texto: "su.prueba", tipo: "azul" },
  PRUEBA_CADUCADA: { texto: "su.pruebaCaducada", tipo: "roja" },
  AL_DIA: { texto: "su.alDia", tipo: "verde" },
  MOROSO: { texto: "su.moroso", tipo: "roja" },
  SUSPENDIDO: { texto: "su.suspendido", tipo: "roja" },
};

export default function Suscripcion() {
  const { t, dias: enDias } = useIdioma();
  const [recibo, setRecibo] = useState<Pago | null>(null);
  const consulta = useQuery({
    queryKey: ["suscripcion-detalle"],
    queryFn: () => api<DatosSuscripcion>("/club/suscripcion"),
  });

  const s = consulta.data;
  if (!s) return null;

  const marca = ETIQUETA_ESTADO[s.estado];
  const gratis = s.cuotaMensualCentimos === 0;

  return (
    <Panel titulo={t("su.titulo")}>
      <div className="fila separada" style={{ marginTop: 0, marginBottom: 10 }}>
        <Etiqueta tipo={marca?.tipo ?? ""}>{marca ? t(marca.texto) : s.estado}</Etiqueta>
        <strong>
          {gratis ? t("su.sinCoste") : t("su.alMes", { importe: euros(s.cuotaMensualCentimos) })}
        </strong>
      </div>

      <p className="suave pequeno" style={{ marginTop: 0 }}>
        {textoDelEstado(t, enDias, s)}
      </p>

      {s.puedeVerPagos && (
        <>
          <h3 style={{ marginBottom: 4 }}>{t("su.pagos")}</h3>
          {s.pagos.length === 0 ? (
            <Vacio>{t("su.sinPagos")}</Vacio>
          ) : (
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("su.fecha")}</th>
                  <th>{t("su.importe")}</th>
                  <th>{t("su.cubreHasta")}</th>
                  <th>{t("su.forma")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {s.pagos.map((p) => (
                  <tr key={p.id}>
                    <td>{fecha(p.fecha)}</td>
                    <td>{euros(p.importeCentimos)}</td>
                    <td>{fecha(p.periodoHasta)}</td>
                    <td>{t(`su.metodo.${p.metodo}` as Clave)}</td>
                    <td style={{ textAlign: "right" }}>
                      <button className="boton fantasma pequeno" onClick={() => setRecibo(p)}>
                        {t("su.recibo")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {recibo && <DialogoRecibo datos={s} pago={recibo} alCerrar={() => setRecibo(null)} />}
    </Panel>
  );
}

function textoDelEstado(
  t: (clave: Clave, variables?: Variables) => string,
  enDias: (n: number) => string,
  s: DatosSuscripcion,
): string {
  if (s.cuotaMensualCentimos === 0) return t("su.sinCuota");
  if (s.bloqueado) return t("su.bloqueado", { dias: enDias(s.diasDeRetraso) });
  if (s.enGracia) {
    return s.diasDeRetraso === 1
      ? t("su.vencioAyer")
      : t("su.vencioHace", { dias: enDias(s.diasDeRetraso) });
  }
  if (s.hasta && s.diasRestantes != null) {
    const cuando =
      s.diasRestantes === 0
        ? t("su.hoy")
        : s.diasRestantes === 1
          ? t("su.manana")
          : t("su.enDias", { dias: enDias(s.diasRestantes) });
    return t(s.estado === "PRUEBA" ? "su.pruebaTermina" : "su.periodoHasta", {
      fecha: fecha(s.hasta),
      cuando,
    });
  }
  return t("su.sinFecha");
}

/**
 * Justificante de un pago.
 *
 * Se llama recibo y no factura a propósito. Una factura necesita numeración
 * correlativa, desglose de IVA y los datos fiscales de quien la emite; esto es
 * el comprobante de que un pago concreto se registró, que es lo que el club
 * necesita para su carpeta. Lo otro se lo tiene que dar quien cobra.
 */
function DialogoRecibo({ datos, pago, alCerrar }: { datos: DatosSuscripcion; pago: Pago; alCerrar: () => void }) {
  const { t } = useIdioma();
  const club = datos.club;
  const direccion = [club.direccion, [club.cp, club.ciudad].filter(Boolean).join(" "), club.provincia]
    .filter(Boolean)
    .join(", ");

  return (
    <Modal
      titulo={t("su.recibo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cerrar")}
          </button>
          <button className="boton" onClick={() => window.print()}>
            {t("su.r.imprimir")}
          </button>
        </>
      }
    >
      <article className="documento">
        <header>
          <h1>{t("su.recibo")}</h1>
          {datos.proveedor ? (
            <>
              <h2>{datos.proveedor.nombre}</h2>
              <p className="datos-club">
                {[datos.proveedor.nif && `NIF ${datos.proveedor.nif}`, datos.proveedor.direccion, datos.proveedor.email]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </>
          ) : (
            <h2>{t("su.r.suscripcion")}</h2>
          )}
        </header>

        <table>
          <tbody>
            <tr>
              <th style={{ width: "35%" }}>{t("su.r.recibidoDe")}</th>
              <td>
                {club.nombre}
                {club.cif ? ` · CIF ${club.cif}` : ""}
                {direccion ? ` · ${direccion}` : ""}
              </td>
            </tr>
            <tr>
              <th>{t("su.r.fechaDelPago")}</th>
              <td>{fecha(pago.fecha)}</td>
            </tr>
            <tr>
              <th>{t("su.r.concepto")}</th>
              <td>{t("su.r.conceptoTexto", { fecha: fecha(pago.periodoHasta) })}</td>
            </tr>
            <tr>
              <th>{t("su.r.formaDePago")}</th>
              <td>{t(`su.metodo.${pago.metodo}` as Clave)}</td>
            </tr>
            <tr>
              <th>{t("su.r.importe")}</th>
              <td>
                <strong>{euros(pago.importeCentimos)}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        <p className="emision">
          {t("su.r.justificante")}
        </p>
      </article>
    </Modal>
  );
}
